'use client';

/**
 * DataToolbar Component
 *
 * Unified toolbar for data list pages combining search, filters, and actions
 * in a single compact row. Replaces scattered implementations across portals.
 *
 * Features:
 * - Compact single-row layout (40-48px height)
 * - Debounced search with clear button
 * - Chip-style inline filter selects
 * - Icon-only action buttons with tooltips
 * - Primary action button (rightmost, prominent)
 * - Active filter count badge with clear all
 * - Responsive collapse behavior
 * - Full i18n support
 * - Neon accent theming
 */

import Box from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type React from 'react';
import { useMemo } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { transitionPresets } from '../../theme/transitions';
import type { DataToolbarProps, ToolbarAction, ToolbarDensity, ToolbarFilter } from '../../types/dataToolbar';
import { GlassIconButton } from '../GlassIconButton';
import { GlassTooltip } from '../GlassTooltip';
import { ToolbarFilterChip } from './ToolbarFilterChip';
import { ToolbarSearch } from './ToolbarSearch';

// ============================================================================
// Icons
// ============================================================================

const AddIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
  </svg>
);

const CloseIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

// Standard action icons mapping
const actionIconMap: Record<string, React.FC<{ size?: number }>> = {
  download: ({ size = 18 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
    </svg>
  ),
  settings: ({ size = 18 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
    </svg>
  ),
  refresh: ({ size = 18 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
  ),
  filter: ({ size = 18 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
    </svg>
  ),
  columns: ({ size = 18 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 18h5V5h-5v13zm-6 0h5V5H4v13zM16 5v13h5V5h-5z" />
    </svg>
  ),
  export: ({ size = 18 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />
    </svg>
  ),
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $density: ToolbarDensity;
  $bordered: boolean;
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark, $density, $bordered }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  padding: theme.spacing($density === 'compact' ? 1 : 1.5),
  minHeight: $density === 'compact' ? 48 : 56,
  flexWrap: 'wrap',

  ...($bordered && {
    borderBottom: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  }),

  // Responsive adjustments
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
}));

const LeftSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  flexWrap: 'wrap',

  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.75),
  },
}));

const FiltersSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  flexWrap: 'wrap',
}));

const ActionsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flexShrink: 0,
}));

interface ClearFiltersBadgeProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const ClearFiltersBadge = styled('button', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ClearFiltersBadgeProps>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const accentColor = neonStyles?.base ?? theme.palette.warning.main;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 24,
    padding: '0 8px',
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: transitionPresets.allFast,
    backgroundColor: alpha(accentColor, $isDark ? 0.15 : 0.1),
    color: accentColor,

    '&:hover': {
      backgroundColor: alpha(accentColor, $isDark ? 0.25 : 0.2),
    },

    '&:focus-visible': {
      outline: `2px solid ${accentColor}`,
      outlineOffset: 1,
    },
  };
});

interface PrimaryActionButtonProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $density: ToolbarDensity;
}

const PrimaryActionButton = styled('button', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<PrimaryActionButtonProps>(({ theme, $isDark, $neonAccent, $density }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const primaryColor = neonStyles?.base ?? theme.palette.primary.main;
  const size = $density === 'compact' ? 32 : 36;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    padding: 0,
    borderRadius: 8,
    cursor: 'pointer',
    transition: transitionPresets.allFast,

    // Outlined style
    background: 'transparent',
    border: `1.5px solid ${alpha(primaryColor, $isDark ? 0.5 : 0.4)}`,
    color: primaryColor,

    '&:hover': {
      backgroundColor: alpha(primaryColor, $isDark ? 0.12 : 0.08),
      borderColor: primaryColor,
    },

    '&:active': {
      backgroundColor: alpha(primaryColor, $isDark ? 0.2 : 0.12),
    },

    '&:focus-visible': {
      outline: `2px solid ${primaryColor}`,
      outlineOffset: 2,
    },

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      borderColor: alpha(primaryColor, 0.2),
    },
  };
});

const Divider = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  width: 1,
  height: 24,
  backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
  margin: '0 4px',
}));

// ============================================================================
// Component
// ============================================================================

export const DataToolbar: React.FC<DataToolbarProps> = ({
  search,
  filters,
  activeFilterCount = 0,
  onClearFilters,
  clearFiltersLabel = 'Clear',
  primaryAction,
  actions,
  density = 'comfortable',
  neonAccent,
  bordered = false,
  className,
  leftSlot,
  rightSlot,
  collapseFiltersAt = 768,
  collapseSearchAt = 480,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Responsive breakpoints
  const isFiltersCollapsed = useMediaQuery(`(max-width:${collapseFiltersAt}px)`);
  const isSearchCollapsed = useMediaQuery(`(max-width:${collapseSearchAt}px)`);

  // Filter visible filters
  const visibleFilters = useMemo(() => filters?.filter((f: ToolbarFilter) => !f.hidden) ?? [], [filters]);

  // Filter visible actions
  const visibleActions = useMemo(() => actions?.filter((a: ToolbarAction) => !a.hidden) ?? [], [actions]);

  // Render action icon
  const renderActionIcon = (action: ToolbarAction) => {
    if (action.icon) {
      return action.icon;
    }
    if (action.type && action.type !== 'custom' && actionIconMap[action.type]) {
      const IconComponent = actionIconMap[action.type];
      return <IconComponent size={density === 'compact' ? 16 : 18} />;
    }
    return null;
  };

  return (
    <StyledContainer $isDark={isDark} $density={density} $bordered={bordered} className={className}>
      <LeftSection>
        {/* Search */}
        {search && !isSearchCollapsed && (
          <ToolbarSearch
            value={search.value}
            onChange={search.onChange}
            placeholder={search.placeholder}
            debounceMs={search.debounceMs}
            disabled={search.disabled}
            autoFocus={search.autoFocus}
            neonAccent={neonAccent}
            density={density}
          />
        )}

        {/* Left slot */}
        {leftSlot}

        {/* Filters */}
        {visibleFilters.length > 0 && !isFiltersCollapsed && (
          <FiltersSection>
            {visibleFilters.map((filter: ToolbarFilter) => (
              <ToolbarFilterChip
                key={filter.key}
                label={filter.label}
                value={filter.value}
                options={filter.options}
                onChange={filter.onChange}
                disabled={filter.disabled}
                neonAccent={neonAccent}
                density={density}
              />
            ))}
          </FiltersSection>
        )}

        {/* Active filter count badge */}
        {activeFilterCount > 0 && onClearFilters && (
          <ClearFiltersBadge
            $isDark={isDark}
            $neonAccent={neonAccent}
            onClick={onClearFilters}
            aria-label={`${clearFiltersLabel} ${activeFilterCount} filters`}
          >
            <CloseIcon size={10} />
            <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
              {activeFilterCount}
            </Typography>
          </ClearFiltersBadge>
        )}

        {/* Right slot */}
        {rightSlot}
      </LeftSection>

      <ActionsSection>
        {/* Secondary actions */}
        {visibleActions.length > 0 && (
          <>
            {visibleActions.map((action: ToolbarAction, index: number) => (
              <GlassTooltip key={index} title={action.tooltip} arrow placement="top">
                <span>
                  <GlassIconButton
                    size={density === 'compact' ? 'small' : 'medium'}
                    variant="standard"
                    color={action.color ?? 'default'}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    aria-label={action.ariaLabel ?? action.tooltip}
                  >
                    {renderActionIcon(action)}
                  </GlassIconButton>
                </span>
              </GlassTooltip>
            ))}
            {primaryAction && <Divider $isDark={isDark} />}
          </>
        )}

        {/* Primary action */}
        {primaryAction && (
          <GlassTooltip title={primaryAction.tooltip} arrow placement="top">
            <span data-astor-id={primaryAction.dataAstorId}>
              <PrimaryActionButton
                $isDark={isDark}
                $neonAccent={neonAccent}
                $density={density}
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                aria-label={primaryAction.ariaLabel ?? primaryAction.tooltip}
                data-testid={primaryAction.dataTestId}
              >
                <AddIcon size={density === 'compact' ? 18 : 20} />
              </PrimaryActionButton>
            </span>
          </GlassTooltip>
        )}
      </ActionsSection>
    </StyledContainer>
  );
};

DataToolbar.displayName = 'DataToolbar';

export default DataToolbar;
